// libraries
import React from 'react'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import objectPath from 'object-path'
import { Link } from 'react-router-dom'

import get from '@st-operations/uikit/lib/get.js'
import pretty from '@st-operations/uikit/lib/pretty.js'

// css
import './styles.scss'

// components
import {
  CheckBox,
  ActionsDropdown,
  IconButton,
  Card,
  Value,
} from '@st-operations/uikit/src/v2/index.js'
import i18n from '@st-operations/uikit/i18n.js'
import Events from '@st-operations/uikit/Events'
import classNames from 'classnames'

// constants
const CHECKBOX_COLUMN_WIDTH = 37
const ACTIONS_COLUMN_WIDTH = 80

export const CHECKBOX_ACTION = {
  ADD: 'add',
  DELETE: 'delete',
  SELECT_ALL_PAGES: 'select-all-pages',
}

// component
class Table extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      selected_ids: new Set(),
    }
    this.table = React.createRef()
  }

  componentDidMount() {
    // check for table's available width
    var interval = setInterval(() => {
      if (this.table && this.table.current && this.table.current.offsetWidth) {
        this.setState({ table_width: this.table.current.offsetWidth })
        clearInterval(interval)
      }
    }, 10)

    const selected_ids = this.props.selection
      ? new Set(this.props.selection)
      : this.state.selected_ids
    this.setState({ selected_ids })
    this.selectionListener = Events.addListener('Table-handle-selected', ({ action, ids = [] }) => {
      switch (action) {
        case CHECKBOX_ACTION.ADD:
          ids.forEach((id) => selected_ids.add(id))
          break
        case CHECKBOX_ACTION.DELETE:
          ids.forEach((id) => selected_ids.delete(id))
          break
      }
      this.setState({ selected_ids })
    })
  }

  componentWillUnmount() {
    // this page remounts the table on every batch action, so a leaked listener
    // would accumulate and keep churning through the (now large) id set
    this.selectionListener?.remove()
  }

  onSelectAll = () => {
    const { data: rows, onCheckboxClick } = this.props
    const { selected_ids } = this.state

    const { isAllSelected } = this.getSelectedAllState()

    // unselect
    if (isAllSelected) {
      const newSelected = new Set()
      onCheckboxClick({
        action: CHECKBOX_ACTION.DELETE,
        items: [...selected_ids],
        currentSelected: [...newSelected],
      })
      this.setState({ selected_ids: newSelected })
      return
    }

    // save new entities in a set and update current set
    const newIdsSet = new Set()
    const newIds = rows.map(({ _id }) => (selected_ids.has(_id) ? null : _id)).filter(Boolean)
    newIds.forEach((id) => {
      newIdsSet.add(id)
      selected_ids.add(id)
    })

    onCheckboxClick({
      action: CHECKBOX_ACTION.ADD,
      items: [...newIdsSet],
      currentSelected: [...selected_ids],
    })

    this.setState({ selected_ids })
  }

  onCheckboxClick = (event) => {
    const { onCheckboxClick } = this.props
    const {
      checked,
      dataset: { id },
    } = event.target
    let { selected_ids } = this.state

    if (checked) {
      selected_ids.add(id)
      onCheckboxClick({
        action: CHECKBOX_ACTION.ADD,
        items: [id],
        currentSelected: [...selected_ids],
      })
    } else {
      selected_ids.delete(id)
      onCheckboxClick({
        action: CHECKBOX_ACTION.DELETE,
        items: [id],
        currentSelected: [...selected_ids],
      })
    }

    this.setState({ selected_ids })
  }

  onRowClick(e, item) {
    const { onRowClick } = this.props
    if (onRowClick) {
      this.props.onRowClick(item)
    }
  }

  getSelectedAllState = () => {
    const { data: rows } = this.props
    const { selected_ids } = this.state

    const notSelectedItems = rows.filter((row) => !selected_ids.has(row._id))
    const isAllSelected = notSelectedItems.length === 0
    const isIndeterminate = !isAllSelected && rows.length !== notSelectedItems.length

    return { isAllSelected, isIndeterminate }
  }

  renderHeaderRow(columns) {
    const {
      has_checkboxes,
      actions,
      showSelectAllPages,
      allPagesSelected,
      allPagesLoading,
      onSelectAllPages,
    } = this.props

    const { isAllSelected, isIndeterminate } = this.getSelectedAllState()

    return (
      <div className="st-table__row">
        <If condition={has_checkboxes}>
          <div
            className="st-table__cell st-table__cell--checkbox"
            style={{ width: CHECKBOX_COLUMN_WIDTH }}
          >
            <CheckBox
              checked={isAllSelected}
              isIndeterminate={isIndeterminate}
              onChange={this.onSelectAll}
            />
          </div>
        </If>
        <For each="column" index="key" of={columns}>
          <div
            className={`st-table__cell ${column.className || ''}`}
            data-sorted={column.sortable && this.props.sort_key == column.key}
            key={`header-${key}`}
            style={{ width: column.width, ...column.styles }}
          >
            {this.renderHeaderCellContent(column)}
            <If condition={key === 0 && showSelectAllPages && isAllSelected}>
              <CheckBox
                className="st-table__select-all-pages"
                checked={allPagesSelected}
                onChange={() => !allPagesLoading && onSelectAllPages(!allPagesSelected)}
                label={allPagesLoading ? 'Selecting all pages…' : 'Select from all pages?'}
                disabled={allPagesLoading}
              />
            </If>
          </div>
        </For>
        <If condition={actions.length > 0}>
          <div
            className="st-table__cell st-table__cell--checkbox z-10"
            style={{ width: ACTIONS_COLUMN_WIDTH }}
          >
            <ActionsDropdown actions={actions} />
          </div>
        </If>
      </div>
    )
  }

  renderHeaderCellContent(column) {
    if (!column) column = {}
    let { className, sortable } = column
    let onSort = () => {
      if (column.sortable && this.props.onSort) {
        this.props.onSort(column.key || column.name)
      }
    }
    return (
      <span className={classnames('st-table__header-cell-content', className)}>
        <span
          className={classNames('st-table__action', {
            'font-bold text-primary': this.props.sort_key === column.key,
          })}
        >
          {i18n(column.name || pretty(column.key))}
        </span>
        <If condition={sortable}>
          <IconButton
            text="Sort"
            ariaLabel="Sort column"
            iconProps={{ icon: 'sort', width: 16, height: 16, color: 'currentColor' }}
            className="st-table__sort-button"
            isActive={this.props.sort_key === column.key}
            onClick={onSort}
          />
        </If>
      </span>
    )
  }

  renderBodyRow(item, columns) {
    const { selected_ids } = this.state
    const { has_checkboxes, actions } = this.props

    return (
      <div
        style={{ cursor: this.props.onRowClick ? 'pointer' : 'default' }}
        className="st-table__row"
        key={`row-${item.index}`}
        onClick={(e) => this.onRowClick(e, item)}
      >
        <If condition={has_checkboxes}>
          <div
            className="st-table__cell st-table__cell--checkbox"
            style={{ width: CHECKBOX_COLUMN_WIDTH }}
            onClick={(e) => e.stopPropagation()}
          >
            <CheckBox
              checked={!!selected_ids.has(item._id)}
              data-id={item._id}
              onChange={this.onCheckboxClick}
            />
          </div>
        </If>
        <For each="column" of={columns}>
          {this.renderBodyCell(item, column)}
        </For>
        <If condition={actions.length > 0}>
          <div
            className="st-table__cell st-table__cell--checkbox"
            style={{ width: ACTIONS_COLUMN_WIDTH }}
          >
            &nbsp;
          </div>
        </If>
      </div>
    )
  }

  renderBodyCell(item, column) {
    let { renderCustomCellContent } = this.props

    // find rendering function
    let render = this.renderBodyCellContent
    if (renderCustomCellContent) {
      render = renderCustomCellContent
    }
    if (column.render) {
      render = column.render
    }
    return (
      <div
        className={`st-table__cell ${column.className || ''}`}
        col={column.index}
        key={`cell-${item.index}-${column.index}`}
        style={{ width: column.width, ...column.styles }}
      >
        <If condition={render}>
          {this.props.showLabel && (
            <span className="st-table__label">{i18n(column.name || pretty(column.key))}:</span>
          )}
          {render(item, column)}
        </If>
      </div>
    )
  }

  renderBodyCellContent(item, column) {
    if (!column) column = {}

    // deep search for value
    let value = objectPath.get(item, column.key)

    // class name
    let { className } = column

    // check for link and href dynamic properties
    let { link, href, onClick } = column
    let convert = (part) => {
      if (part[0] == ':') {
        return get(item, part.slice(1))
      }
      return part
    }
    if (link && link.includes(':')) {
      link = link.split('/').map(convert).join('/')
    }
    if (href && href.includes(':')) {
      href = href.split('/').map(convert).join('/')
    }

    const key = `render-${item.index}-${column.index}`
    const renderValue = <Value format={column.format} value={value} />

    return (
      <Choose>
        <When condition={link}>
          <Link to={link} key={key}>
            {renderValue}
          </Link>
        </When>
        <When condition={href}>
          <a href={href} target="_blank" rel="noopener noreferrer" key={key}>
            {renderValue}
          </a>
        </When>
        <Otherwise>
          <span className={className} onClick={(e) => onClick && onClick(e, item)} key={key}>
            {renderValue}
          </span>
        </Otherwise>
      </Choose>
    )
  }

  render() {
    let { className, style, tableName } = this.props
    let { columns, data: rows, has_checkboxes } = this.props
    let { table_width } = this.state

    if (!rows) {
      return <div />
    }

    if (!columns) {
      return <div />
    }

    // set column index
    columns.forEach((col, index) => {
      col.index = index
    })

    // set data index
    rows.forEach((item, index) => {
      item.index = index
    })

    // compute column widths
    if (table_width) {
      let available = table_width

      // safe requested space for specific columns
      let count = columns.length
      columns.forEach((col) => {
        if (col.width) {
          available -= col.width
          count--
        }
      })

      // split remaining space among the other columns
      columns.forEach((col) => {
        if (!col.width) {
          col.width = Math.floor(available / count)
        }
      })
    }

    return (
      <div
        ref={this.table}
        className={`st-table ${className || ''}`}
        style={style}
        data-has-checkboxes={has_checkboxes || null}
      >
        <Card noHover className="st-table__box">
          <div className="st-table__header">{this.renderHeaderRow(columns)}</div>
          <div className="st-table__body">
            <If condition={rows.length === 0}>
              <div className="st-table__no-results">No results</div>
            </If>
            <If condition={rows.length > 0}>
              <For each="item" of={rows}>
                {this.renderBodyRow(item, columns)}
              </For>
            </If>
          </div>
        </Card>
      </div>
    )
  }
}

Table.propTypes = {
  onCheckboxClick: PropTypes.func,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      onClick: PropTypes.func,
      icon: PropTypes.shape({ icon: PropTypes.string, type: PropTypes.string }),
    })
  ),
  showLabel: PropTypes.bool,
}

Table.defaultProps = {
  actions: [],
  showLabel: false,
  onCheckboxClick: () => {},
  showSelectAllPages: false,
  allPagesSelected: false,
  allPagesLoading: false,
  onSelectAllPages: () => {},
}

// export
export default Table
